import React, { Suspense } from "react";
import PropTypes from "prop-types";
import { Flex, Box, BREAKPOINTS } from "orcs-design-system";
import { useLocation, withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import { themeGet } from "@styled-system/theme-get";

import useEmbedMode from "src/hooks/useEmbedMode";
import { useEnforceLightThemeWhenDarkSwitcherDisabled } from "src/hooks/useEnforceLightThemeWhenDarkSwitcherDisabled";
import useEmbedHoverDetection from "src/hooks/useEmbedHoverDetection";
import useEmbedLinkInterceptor from "src/hooks/useEmbedLinkInterceptor";
import { useAppHeadersHeightCalc } from "src/hooks/useAppHeadersHeightCalc";
import { removeEmbedParams } from "src/util/embedUtils";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { GoalsExploreProvider } from "src/contexts/goalsExploreContext";
import Sidebar from "src/sidebar/Sidebar";
import Loading from "src/components/LoadingPage";
import AppNavSimple from "../components/Nav/AppNavSimple";
import OutdatedBrowserMessage from "../components/OutdatedBrowserMessage";
import { useAISidebar } from "src/hooks/useAISidebar";
import useResizableChatPaneWidth from "src/components/AIContent/v3/hooks/useResizableChatPaneWidth";

// Lazy: AI-off tenants must not load AgentCore chat / streamAIResponse on boot.
const AIChatV3Provider = React.lazy(() =>
  import("src/components/AIContent/v3/AIChatV3Provider").then((m) => ({
    default: m.AIChatV3Provider,
  }))
);
const AINav = React.lazy(() =>
  import("src/components/AINav").then((m) => ({ default: m.AINav }))
);

// Hoist the unified chat provider above the route outlet (only for v2
// workspaces) so the SSE stream and chat thread survive navigation between the
// sidebar and the dashboard canvas. v1 workspaces keep their per-window
// provider in AIChatV3Window.
const MaybeAIChatProvider = ({ enabled, children }) =>
  enabled ? (
    <Suspense fallback={null}>
      <AIChatV3Provider>{children}</AIChatV3Provider>
    </Suspense>
  ) : (
    children
  );

MaybeAIChatProvider.propTypes = {
  enabled: PropTypes.bool,
  children: PropTypes.node,
};

// Share goals tree state between Explore sidenav and Goals Explorer route.
const MaybeGoalsExploreProvider = ({ enabled, workspaceId, children }) =>
  enabled ? (
    <GoalsExploreProvider key={workspaceId}>{children}</GoalsExploreProvider>
  ) : (
    children
  );

MaybeGoalsExploreProvider.propTypes = {
  enabled: PropTypes.bool,
  workspaceId: PropTypes.string,
  children: PropTypes.node,
};

const SidebarWrapper = styled(Flex).attrs({
  forwardedAs: "nav",
  flexDirection: "column",
})`
  align-self: stretch;
  height: 100%;
  min-height: 0;
`;

const Content = styled(Flex).attrs({
  forwardedAs: "section",
  flexDirection: "column",
})`
  overflow-y: auto !important;
  height: auto;
  padding: ${themeGet("space.r")};
  background: transparent;
  flex: 1;
  min-width: 0;
  z-index: 2;
  ${({ $isEmbedMode }) =>
    !$isEmbedMode &&
    css`
      padding-top: 0;
      margin-top: ${themeGet("space.r")};
    `}
  ${({ $aiViewMode }) =>
    $aiViewMode === "fullscreen" &&
    css`
      display: none;
    `}
`;

const AppMain = styled(Flex).attrs({
  forwardedAs: "main",
  alignItems: "stretch",
})`
  flex: 1;
  height: ${({ $appHeadersHeightCalc }) =>
    $appHeadersHeightCalc || "calc(100vh - 64px)"};
  overflow: hidden;
  position: relative;
  @media screen and (max-width: ${BREAKPOINTS.SMALL_SCREEN}px) {
    ${({ $isEmbedMode }) =>
      !$isEmbedMode &&
      css`
        padding-bottom: 50px;
      `}
  }
  ${({ $compactShell, $isEmbedMode }) =>
    $compactShell &&
    !$isEmbedMode &&
    css`
      padding-bottom: 50px;
    `}
`;

const TopNavContainer = styled(Box)`
  z-index: 10;
  box-shadow: 0 0px 10px rgba(0, 0, 0, 0.1);
`;

const SkipNavLink = styled(Box).attrs({ forwardedAs: "a" })`
  position: absolute;
  left: -9999px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
  z-index: 100;
  padding: ${themeGet("space.s")} ${themeGet("space.l")};
  background: ${themeGet("colors.primary")};
  color: ${themeGet("colors.white")};
  font-size: ${themeGet("fontSizes.1")};
  text-decoration: none;
  border-radius: 0 0 ${themeGet("radii.1")} 0;

  &:focus {
    position: fixed;
    top: 0;
    left: 0;
    width: auto;
    height: auto;
  }
`;

const EmbedTooltip = styled.div`
  position: fixed;
  top: ${({ mouseY, isVisible }) => {
    if (!isVisible || !mouseY) {
      return "-100px";
    }
    return `${mouseY - 60}px`;
  }};
  left: ${({ mouseX, isVisible }) => {
    if (!isVisible || !mouseX) {
      return "-100px";
    }
    return `${mouseX + 10}px`;
  }};
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  z-index: 9999;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  pointer-events: none;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  max-width: 300px;
`;

const EmbedInteractionBlocker = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  pointer-events: none;

  &:hover {
    opacity: 0.05;
  }
`;

const AppLayout = ({ emptyMode = false, children, themeSwitcher }) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const aiViewMode = query.get("aiViewMode");
  const { isEmbedMode, hideHeader, hideSidebar } = useEmbedMode();
  const workspace = useWorkspace();
  const { enableAIChat, enableGoalsExplorer } = useWorkspaceFeatureFlags();
  const { isOpen: isChatOpen } = useAISidebar();
  const { isSmallScreen: isCompactShell } = useResizableChatPaneWidth(
    Boolean(enableAIChat) && isChatOpen
  );
  const { hoveredUrl, isHovering, mousePosition } = useEmbedHoverDetection();
  const appHeadersHeightCalc = useAppHeadersHeightCalc({
    includeProfileHeaders: false,
    hideHeader,
  });

  useEmbedLinkInterceptor();
  useEnforceLightThemeWhenDarkSwitcherDisabled();

  const handleEmbedOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      const urlToOpen = hoveredUrl || window.location.href;
      const cleanUrl = removeEmbedParams(urlToOpen);
      window.open(cleanUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Flex
      bg="appBackground"
      flexDirection="column"
      height="100vh"
      overflow="hidden"
      data-testid="app-shell"
    >
      <SkipNavLink href="#appContent">Skip to main content</SkipNavLink>
      {!hideHeader && (
        <TopNavContainer id="app-layout-header">
          <OutdatedBrowserMessage />
          <AppNavSimple emptyMode={emptyMode} themeSwitcher={themeSwitcher} />
        </TopNavContainer>
      )}
      <AppMain
        $appHeadersHeightCalc={appHeadersHeightCalc}
        $isEmbedMode={isEmbedMode}
        $compactShell={isCompactShell}
      >
        <MaybeGoalsExploreProvider
          enabled={enableGoalsExplorer}
          workspaceId={workspace?.id}
        >
          {!hideSidebar && (
            <SidebarWrapper aria-label="Main navigation">
              <Sidebar />
            </SidebarWrapper>
          )}

          <MaybeAIChatProvider enabled={enableAIChat}>
            <Content
              id="appContent"
              $isEmbedMode={isEmbedMode}
              $aiViewMode={aiViewMode}
            >
              <Suspense fallback={<Loading />}>{children}</Suspense>
            </Content>
            {!isEmbedMode && enableAIChat ? (
              <Suspense fallback={null}>
                <AINav />
              </Suspense>
            ) : null}
          </MaybeAIChatProvider>
        </MaybeGoalsExploreProvider>
      </AppMain>
      {isEmbedMode && (
        <>
          <EmbedInteractionBlocker
            onClick={handleEmbedOverlayClick}
            data-embed-overlay="true"
          />
          <EmbedTooltip
            isVisible={isHovering && hoveredUrl}
            mouseX={mousePosition.x}
            mouseY={mousePosition.y}
            data-embed-overlay="true"
          >
            Open in TeamForm
          </EmbedTooltip>
        </>
      )}
    </Flex>
  );
};

AppLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  emptyMode: PropTypes.bool,
  themeSwitcher: PropTypes.func,
};

export default withRouter(AppLayout); // TODO: move to react hooks
